import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';

import { GTMService } from '@services';
import {
  AddressDetails,
  AuthStore,
  GatewayCommissioningStore,
  InstallationDetailsStore,
  InstallationType,
  OwnerDetails,
  RemoteConfigStore,
} from '@store';

import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { PartnerAccessShareDeviceModalComponent } from '../../partner-access-overlay/partner-access-share-device-modal/partner-access-share-device-modal.component';
import { matTooltipSetter } from '../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { TransferInstallationDialogComponent } from './transfer-installation-dialog/transfer-installation-dialog.component';
import { EditOwnerCardDialogComponent } from './edit-owner-card-dialog/edit-owner-card-dialog.component';
import { OwnershipType, PaidFeature } from '../../../../../projects/client';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { FormatPhoneNumberPipe } from './../../../../shared/pipes/format-phone-number/format-phone-number.pipe';
import { ViConfirmDirective } from '@vi-iot/ui/index';
import { PanelModule } from './../../../../shared-ui/panel/panel.module';
import { MatIconModule } from '@angular/material/icon';
import { DashPipe } from '@pipes';
import { LetDirective } from '@ngrx/component';

@Component({
  selector: 'app-owner-card',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    DashPipe,
    FormatPhoneNumberPipe,
    ViConfirmDirective,
    RouterLink,
    PanelModule,
    MatIconModule,
    MatTooltipModule,
    LetDirective,
  ],
  templateUrl: './owner-card.component.html',
  styleUrls: ['./owner-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerCardComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  OwnershipType = OwnershipType;
  public installationType: InstallationType;
  public ownershipType: OwnershipType;
  public vitocrossalOwnerCardEdit: boolean;
  public isVitocrossalNAType: boolean;
  public address$: Observable<AddressDetails>;
  public owner$: Observable<OwnerDetails>;
  public showShareDeviceMonitoringButton: boolean;
  private transferInstallationDialogRef: MatDialogRef<TransferInstallationDialogComponent>;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private isDemo = false;

  installationId: string;

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public authStore: AuthStore,
    public gatewayCommissioningStore: GatewayCommissioningStore,
    public remoteConfigStore: RemoteConfigStore,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private gtmService: GTMService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.installationId = this.activatedRoute.snapshot.params['id'];
    this.authStore.isDemo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isDemo) => {
      this.isDemo = isDemo;
    });
    this.initSubscriptions();
  }

  editOwner(event: Event): void {
    event.stopImmediatePropagation();
    this.dialog.open(EditOwnerCardDialogComponent, {
      width: '552px',
      panelClass: 'new-user-dialog',
      disableClose: true,
    });
  }

  openTransferInstallationModal(event: MouseEvent): void {
    event.stopPropagation();
    this.transferInstallationDialogRef = this.dialog.open(TransferInstallationDialogComponent, {
      width: '715px',
      panelClass: 'transfer-installation-dialog',
    });
  }

  onStopMonitoring(ownerName: string): void {
    if (!this.isDemo) {
      this.installationDetailsStore.stopMonitoring(ownerName);

      this.gtmService.track('onConfirm', 'Cancel monitoring installation', 'deviceCard');
    }
  }

  onCancelStopMonitoring(): void {
    this.gtmService.track('onCancel', 'Cancel monitoring installation', 'deviceCard');
  }

  ngOnDestroy(): void {
    this.setRouteData('users/:id', null);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onRouteChange(owner: OwnerDetails): void {
    this.authStore
      .isRsaUser()
      .pipe(withLatestFrom(this.installationDetailsStore.device$), takeUntil(this.ngUnsubscribe))
      .subscribe(([, device]) => {
        const routeData = {
          installationId: this.installationId,
          deviceName: device.shortName,
          ownerName: owner.name,
        };

        this.setRouteData('users/:id', routeData);
      });
  }

  openShareDeviceMonitoringDialog(): void {
    this.dialog.open(PartnerAccessShareDeviceModalComponent, {
      panelClass: 'partner-access-share-device-dialog',
      autoFocus: false,
      disableClose: true,
    });
  }

  private initSubscriptions(): void {
    this.installationDetailsStore.installationType$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((installationType) => {
        this.installationType = installationType;
        if (installationType === InstallationType.Commercial) {
          this.address$ = this.installationDetailsStore.consumerAddress$;
          this.owner$ = this.installationDetailsStore.consumer$;
        } else {
          this.address$ = this.installationDetailsStore.address$;
          this.owner$ = this.installationDetailsStore.owner$;
        }
      });

    this.installationDetailsStore.ownershipType$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((ownershipType) => {
        this.ownershipType = ownershipType;
        if (
          ownershipType === OwnershipType.CommercialV1 ||
          ownershipType === OwnershipType.CommercialV2
        ) {
          this.address$ = this.installationDetailsStore.consumerAddress$;
          this.owner$ = this.installationDetailsStore.consumer$;
        } else {
          this.address$ = this.installationDetailsStore.address$;
          this.owner$ = this.installationDetailsStore.owner$;
        }
      });

    this.closeModalOnSendInvitationSuccess();

    combineLatest([
      this.installationDetailsStore.ownershipType$,
      this.authStore.isUserOfCommercialOwnerCompanyWithRoleInvitation$,
      this.authStore.checkAvailableGlobalPaidFeaturePresence(PaidFeature.UserManagement),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([ownershipType, isUserWithRoleInvitation, isUserManagement]) => {
        this.showShareDeviceMonitoringButton =
          (ownershipType === OwnershipType.CommercialV1 ||
            ownershipType === OwnershipType.CommercialV2) &&
          isUserWithRoleInvitation &&
          isUserManagement;
      });
  }

  private setRouteData(path: string, data: any): void {
    const route = this.router.config.find((r) => r.path === path);

    route.data = data;
  }

  private closeModalOnSendInvitationSuccess(): void {
    this.gatewayCommissioningStore.state$
      .pipe(
        filter((data) => data.transferInstallationSuccess && !!this.transferInstallationDialogRef),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.transferInstallationDialogRef.close();
      });
  }
}
